import Cookies from 'js-cookie'
import { createFileRoute, redirect, Outlet } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { useAuthStore } from '@/stores/authStore';
import { cn } from '@/lib/utils'
import { SearchProvider } from '@/context/search-context'
import { SidebarDataProvider } from '@/components/layout/data/sidebar-context'
import { SidebarProvider } from '@/components/ui/sidebar'
import { AppSidebar } from '@/components/layout/app-sidebar'
import SkipToMain from '@/components/skip-to-main'
import i18n, { loadAuthenticatedTranslations, loadLanguageFromBackend } from '@/i18n'
import { Loader2 } from 'lucide-react';
import { Header } from '@/components/layout/header';
import { SubscriptionBanner } from '@/components/billing/subscription-banner';

export const Route = createFileRoute('/_authenticated')({
  beforeLoad: async ({ location }) => {
    const auth = useAuthStore.getState().auth;
    if (auth.accessToken == null) {
      throw redirect({
        to: '/auth/login',
        search: {
          redirect: location.href,
        },
      });
    }
  },
  component: RouteComponent
})

function RouteComponent() {
  const defaultOpen = Cookies.get('sidebar:state') !== 'false'
  const [translationsLoaded, setTranslationsLoaded] = useState(false)

  // Load authenticated translations when the authenticated route is mounted
  // Load authenticated translations and user data when the authenticated route is mounted
  useEffect(() => {
    if (localStorage.getItem('accessToken')) {
      setTranslationsLoaded(false)

      // Load user data to ensure permissions are up to date
      const loadData = async () => {
        try {
          // Fetch user details in parallel with language preference
          const [userData] = await Promise.all([
            import('@/api/auth').then(m => m.me()),
            loadLanguageFromBackend()
          ]);

          if (userData) {
            useAuthStore.getState().auth.setUser(userData);
          }

          await loadAuthenticatedTranslations();
          setTranslationsLoaded(true);
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Error loading initial data:', error);
          // If me() fails, we might still have stale user data, so likely just auth failure
          // But we let the error flow handle it or just show UI
          setTimeout(() => setTranslationsLoaded(true), 500)
        }
      };

      loadData();
    } else {
      setTranslationsLoaded(true)
    }
  }, []);

  // Show loading indicator while translations are being loaded
  if (!translationsLoaded) {
    return (
      <div className="h-screen w-full flex items-center justify-center">
        <div className="flex flex-col items-center gap-2">
          <Loader2 className="h-10 w-10 animate-spin text-primary" />
          <p className="text-muted-foreground text-sm">{i18n.t('loading')}</p>
        </div>
      </div>
    )
  }

  return (
    <SearchProvider>
      <SidebarDataProvider>
        <SidebarProvider defaultOpen={defaultOpen}>
          <SkipToMain />
          <AppSidebar />
          <div
            id='content'
            className={cn(
              'ms-auto w-full max-w-full',
              'peer-data-[state=collapsed]:w-[calc(100%-var(--sidebar-width-icon)-1rem)]',
              'peer-data-[state=expanded]:w-[calc(100%-var(--sidebar-width))]',
              'transition-[width] duration-200 ease-linear',
              'flex h-svh flex-col',
              'group-data-[scroll-locked=1]/body:h-full',
              'group-data-[scroll-locked=1]/body:has-[main.fixed-main]:h-svh'
            )}
          >
            <Header fixed>

              <div className='flex items-center gap-3 ms-auto'>


              </div>
            </Header>
            <div className="flex-1 w-full pt-16">
              <SubscriptionBanner />
              <Outlet />
            </div>
          </div>
        </SidebarProvider>
      </SidebarDataProvider>
    </SearchProvider>
  )
}
